/**
 * HelpDialog — overview of Previs Pro Timeline for new users.
 * Opened from the (?) button in the transport bar.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store/uiStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import styles from './HelpDialog.module.css';

function ReviewHelpContent() {
  const { t } = useTranslation('help');
  return (
    <>
      <div className={styles.intro}>
        {t('reviewIntro')}
      </div>

      {/* ── Navigation ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionNavigation')}</div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardPlaybackTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardPlaybackDesc') }} />
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardScenesTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardScenesDesc')}
            </div>
          </div>
        </div>
      </div>

      {/* ── Feedback ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionGivingFeedback')}</div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardApprovalStatusTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardApprovalStatusDesc') }} />
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardCommentsTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardCommentsDesc') }} />
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardDrawingAnnotationsTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardDrawingAnnotationsDescReview') }} />
          </div>
        </div>
      </div>

      <div className={styles.divider} />

      {/* ── Key shortcuts ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionKeyShortcuts')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>Space</kbd> {t('shortcutPlayPause')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>J K L</kbd> {t('shortcutShuttle')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>&larr; &rarr;</kbd> {t('shortcutStepShots')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>C</kbd> {t('shortcutToggleComments')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>D</kbd> {t('shortcutDrawMode')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>F</kbd> {t('shortcutFullscreen')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>?</kbd> {t('shortcutHelpScreen')}</div>
      </div>
    </>
  );
}

function CreatorHelpContent() {
  const { t } = useTranslation('help');
  return (
    <>
      <div className={styles.intro}>
        {t('creatorIntro')}
      </div>

      {/* ── Scenes & Shots ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionScenesAndShots')}</div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardSceneSelectorTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardSceneSelectorDesc')}
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardShotLaneTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardShotLaneDesc')}
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>&#x25C7;</div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardTransitionsTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardTransitionsDesc')}
            </div>
          </div>
        </div>
      </div>

      {/* ── Comments & Review ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionCommentsAndReview')}</div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardCommentPanelTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardCommentPanelDesc') }} />
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardDrawingAnnotationsTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardDrawingAnnotationsDescCreator') }} />
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardApprovalStatusCreatorTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardApprovalStatusCreatorDesc')}
            </div>
          </div>
        </div>
      </div>

      {/* ── AI Generation ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionAiGeneration')}</div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M10 2 L11.5 7.5 L17 9 L11.5 10.5 L10 16 L8.5 10.5 L3 9 L8.5 7.5 Z" fill="currentColor" />
              <path d="M15 2 L15.5 3.5 L17 4 L15.5 4.5 L15 6 L14.5 4.5 L13 4 L14.5 3.5 Z" fill="currentColor" opacity="0.6" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardAiGutterTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardAiGutterDesc', { optI: '\u2325I', optV: '\u2325V', optS: '\u2325S' }) }} />
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardEditOverlayTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardEditOverlayDesc') }} />
          </div>
        </div>
      </div>

      {/* ── Audio ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionAudio')}</div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>&#x1F3B5;</div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardMixerTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardMixerDesc')}
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardDialogTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardDialogDesc')}
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>&#x266B;</div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardMusicTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardMusicDesc')}
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>&#x26A1;</div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardSfxTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardSfxDesc')}
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>&#x1F399;</div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardVoiceoverTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardVoiceoverDesc')}
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardClipControlsTitle')}</div>
            <div className={styles.cardDesc}>
              {t('cardClipControlsDesc')}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sharing ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionSharing')}</div>
        <div className={styles.card}>
          <div className={styles.cardIcon}>&#x1F517;</div>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{t('cardShareForReviewTitle')}</div>
            <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: t('cardShareForReviewDesc') }} />
          </div>
        </div>
      </div>

      <div className={styles.divider} />

      {/* ── Key shortcuts ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('sectionKeyShortcuts')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>Space</kbd> {t('shortcutPlayPause')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>J K L</kbd> {t('shortcutShuttle')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>&larr; &rarr;</kbd> {t('shortcutStepShots')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>C</kbd> {t('shortcutToggleComments')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>D</kbd> {t('shortcutDrawMode')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>E</kbd> {t('shortcutEnhancedImages')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>F</kbd> {t('shortcutFullscreen')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>H</kbd> {t('shortcutGenerationHistory')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>R</kbd> {t('shortcutStepMode')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>⌥A</kbd> {t('shortcutAudioWorkspace')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>⌥I</kbd> {t('shortcutQuickImage')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>⌥V</kbd> {t('shortcutQuickVideo')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>⌥S</kbd> {t('shortcutQuickSpeech')}</div>
        <div className={styles.hintRow}><kbd className={styles.hintKey}>?</kbd> {t('shortcutAllShortcuts')}</div>
      </div>

      <div className={styles.footer} dangerouslySetInnerHTML={{ __html: t('helpFooter') }} />
    </>
  );
}

export function HelpDialog() {
  const open = useUiStore(s => s.helpDialogOpen);
  const close = useUiStore(s => s.closeHelpDialog);
  const readOnly = useProjectStore(s => s.readOnly);
  const { t } = useTranslation('help');

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, close]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={close}>
      <div className={`${styles.dialog} ${readOnly ? styles.dialogReview : ''}`} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>{readOnly ? t('helpDialogTitleReview') : t('helpDialogTitleCreator')}</span>
          <button className={styles.closeBtn} onClick={close}>&times;</button>
        </div>

        <div className={styles.body}>
          {readOnly ? <ReviewHelpContent /> : <CreatorHelpContent />}
        </div>
      </div>
    </div>
  );
}
